import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Flex,
  Icon,
  IconButton,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverTrigger,
  Text,
  Textarea,
  VStack,
  Portal
} from "@chakra-ui/react";
import { MdComment } from "react-icons/md";
import { buildCommentEntry, formatCommentDate, normalizeComments } from "./commentUtils";

export default function CommentPopover({ product, isAdmin, onSave, size = "sm", compact = false }) {
  const [commentText, setCommentText] = useState("");
  const [comments, setComments] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    setComments(normalizeComments(product?.comments || []));
  }, [product?.comments, product?.pd_id]);

  const previewComments = useMemo(() => comments.slice(-3).reverse(), [comments]);

  const handleAddComment = async () => {
    const entry = buildCommentEntry(commentText);
    if (!entry.text) return;

    setIsSubmitting(true);
    const nextComments = [...comments, entry];
    setComments(nextComments);
    setCommentText("");
    try {
      await onSave?.(nextComments);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Popover isOpen={isOpen} onClose={() => setIsOpen(false)} placement="left" isLazy>
      <PopoverTrigger>

    <Box
  position="relative"
  display="inline-flex"
  alignItems="center"
  justifyContent="center"
  m="2"
  w="28px"
  h="28px"
  borderRadius="md"
  bg={comments.length > 0 ? "blue.50" : "transparent"}
  color={comments.length > 0 ? "blue.600" : "gray.600"}
  cursor="pointer"
  _hover={{
    bg: comments.length > 0 ? "blue.100" : "gray.100",
  }}
  onClick={() => setIsOpen((prev) => !prev)}
>
  <MdComment size="17px" />

  {comments.length > 0 && (
    <Box
      position="absolute"
      top="3px"
      right="3px"
      w="5px"
      h="5px"
      borderRadius="full"
      bg="blue.500"
    />
  )}
</Box>
      </PopoverTrigger>
      <Portal>

        <PopoverContent w="280px" maxW="90vw" zIndex={2000}>
          <PopoverArrow />
          <PopoverCloseButton />
          <PopoverBody p={3}>
            <VStack align="stretch" spacing={3}>
              <Box>
                <Text fontSize="sm" fontWeight="semibold">Comments</Text>
                {comments.length === 0 ? (
                  <Text fontSize="xs" color="gray.500" mt={1}>No comments yet.</Text>
                ) : (
                  <VStack align="stretch" spacing={2} mt={2}>
                    {previewComments.map((comment, index) => (
                      <Box key={`${comment.created_at}-${index}`} borderWidth="1px" borderRadius="md" p={2}>
                        <Text fontSize="xs" color="gray.500">{formatCommentDate(comment.created_at)}</Text>
                        <Text fontSize="sm" mt={1}>{comment.text}</Text>
                      </Box>
                    ))}
                  </VStack>
                )}
              </Box>

              {isAdmin && (
                <Box>
                  <Textarea
                    size="sm"
                    placeholder="Add a comment"
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    minH="80px"
                  />
                  <Flex justify="flex-end" mt={2}>
                    <Button size="sm" colorScheme="brand" onClick={handleAddComment} isLoading={isSubmitting}>
                      Add comment
                    </Button>
                  </Flex>
                </Box>
              )}
            </VStack>
          </PopoverBody>
        </PopoverContent>
      </Portal>

    </Popover>
  );
}
